import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';



const Alert = ({ children, className }) => {
    return (
        <div className={`p-4 rounded-lg border ${className}`}>
            <div className="flex gap-3">{children}</div>
        </div>
    );
};

const AlertDescription = ({ children, className }) => {
    return <div className={`text-sm ${className}`}>{children}</div>;
};

const Card = ({ children, className }) => (
    <div className={`bg-white shadow-lg rounded-lg ${className}`}>{children}</div>
);

const CardHeader = ({ children, className }) => (
    <div className={`p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className }) => (
    <h2 className={`text-2xl font-bold ${className}`}>{children}</h2>
);

const CardContent = ({ children }) => (
    <div className="p-6">{children}</div>
);

const PasswordResetForm = () => {
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const validatePassword = (password) => {
        return {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        const validation = validatePassword(formData.password);
        if (!Object.values(validation).every(Boolean)) {
            setError('Please ensure all password requirements are met');
            return;
        }

        setIsSubmitted(true);
    };

    const validation = validatePassword(formData.password);

    return (
        <>
            <SEO
              title="Reset Password"
              description="Reset your ProducerBuddy account password to regain access to your film production tools."
              url="https://producerbuddy.pilomation.com/reset-password"
              robots="noindex, nofollow"
            />
            <Nav />
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle>Reset Your Password</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isSubmitted ? (
                            <Alert className="bg-green-50 border-green-200">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                                <AlertDescription className="text-green-700">
                                    Password successfully reset! You can now log in with your new password.
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <Alert className="bg-red-50 border-red-200">
                                        <XCircle className="h-5 w-5 text-red-500" />
                                        <AlertDescription className="text-red-700">{error}</AlertDescription>
                                    </Alert>
                                )}
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        New Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Confirm Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <p className="text-sm font-medium text-gray-700">Password must contain:</p>
                                    <ul className="space-y-1 text-sm">
                                        <li className={`flex items-center ${validation.length ? 'text-green-600' : 'text-gray-500'}`}>
                                            {validation.length ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                                            At least 8 characters
                                        </li>
                                        <li className={`flex items-center ${validation.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                                            {validation.uppercase ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                                            One uppercase letter
                                        </li>
                                        <li className={`flex items-center ${validation.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                                            {validation.lowercase ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                                            One lowercase letter
                                        </li>
                                        <li className={`flex items-center ${validation.number ? 'text-green-600' : 'text-gray-500'}`}>
                                            {validation.number ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                                            One number
                                        </li>
                                        <li className={`flex items-center ${validation.special ? 'text-green-600' : 'text-gray-500'}`}>
                                            {validation.special ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                                            One special character
                                        </li>
                                    </ul>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Reset Password
                                </button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

export default PasswordResetForm;

